import type { Faction, GamePhase, GameStatus, Vote, WinReason } from './enums';
import type { PrivatePlayerState, PublicGameState, SpeechClaimInput } from './state';
import type { ThemeId } from './theme';

// Socket.IO contract (spec §69–§70). Every client event carries a payload and
// an acknowledgement callback; the server answers every request through it.
//
// State is synchronized, not streamed as deltas: after any change each player
// receives the public `game:state` and their own `role:privateState`. Private
// hands, research results and polling results are fields of the private state,
// so they only ever travel to the one player entitled to them.

export type AckError = { code: string; message: string };
export type Ack<T = null> = { ok: true; data: T } | { ok: false; error: AckError };
export type AckFn<T = null> = (response: Ack<T>) => void;

/** Returned when a browser gains a seat. `token` is present only when newly issued. */
export interface SessionInfo {
  code: string;
  gameId: string;
  playerId: string;
  name: string;
  token?: string;
}

export interface RosterPlayer {
  id: string;
  name: string;
  seat: number;
  connected: boolean;
  isHost: boolean;
}

/** The public view of a game, lobby or in progress. Identical for every player. */
export interface GameView {
  code: string;
  /** Which variant this game uses; every phone renders its words. */
  themeId: ThemeId;
  status: GameStatus;
  paused: boolean;
  hostPlayerId: string | null;
  players: RosterPlayer[];
  settings: GameSettings;
  /** Countdown for the current phase, if it is timed. */
  timer: PhaseTimerView | null;
  /** Null until the game starts. */
  game: PublicGameState | null;
}

export interface GameSettings {
  /** Maximum length of each Question Period. */
  questionPeriodSeconds: number;
}

export const QUESTION_PERIOD_CHOICES = [60, 120, 180, 300] as const;

/**
 * Remaining time as of when the server sent it. Clients add it to their own
 * clock on receipt, so wrong phone clocks don't matter.
 */
export interface PhaseTimerView {
  phase: GamePhase;
  remainingMs: number;
  totalMs: number;
  paused: boolean;
}

export interface ReclaimRequest {
  requestId: string;
  playerId: string;
  playerName: string;
}

export type SessionEndReason = 'KICKED' | 'SEAT_RECLAIMED' | 'LEFT' | 'GAME_DELETED';

type Empty = Record<string, never>;

export interface ClientToServerEvents {
  'game:create': (p: { name: string; themeId?: ThemeId }, ack: AckFn<SessionInfo>) => void;
  'game:join': (p: { code: string; name: string }, ack: AckFn<SessionInfo>) => void;
  'game:reconnect': (p: { code: string; name: string; token: string }, ack: AckFn<SessionInfo>) => void;
  'game:start': (p: Empty, ack: AckFn) => void;
  'game:leave': (p: Empty, ack: AckFn) => void;
  'game:pause': (p: Empty, ack: AckFn) => void;
  'game:resume': (p: Empty, ack: AckFn) => void;

  'lobby:kick': (p: { playerId: string }, ack: AckFn) => void;
  'lobby:settings': (p: { questionPeriodSeconds: number }, ack: AckFn) => void;
  'seat:reclaimRequest': (p: { code: string; name: string }, ack: AckFn<{ requestId: string }>) => void;
  'seat:reclaimDecision': (p: { requestId: string; approve: boolean }, ack: AckFn) => void;

  'role:acknowledge': (p: Empty, ack: AckFn) => void;
  /** Host: stop waiting for players who haven't acknowledged their role. */
  'role:skipReveal': (p: Empty, ack: AckFn) => void;

  'government:nominatePremier': (p: { premierPlayerId: string }, ack: AckFn) => void;
  'government:vote': (p: { vote: Vote }, ack: AckFn) => void;

  'policy:ministerDiscard': (p: { cardIndex: number }, ack: AckFn) => void;
  'policy:premierEnact': (p: { cardIndex: number }, ack: AckFn) => void;
  'policy:requestVeto': (p: Empty, ack: AckFn) => void;
  'policy:vetoDecision': (p: { accept: boolean }, ack: AckFn) => void;

  'power:selectPlayer': (p: { playerId: string }, ack: AckFn) => void;
  'power:acknowledgePeek': (p: Empty, ack: AckFn) => void;

  /** Minister or Premier: what they say happened (truth optional). */
  'speech:give': (p: { claim: SpeechClaimInput }, ack: AckFn) => void;
  /** Vote to end Question Period early (or withdraw with end: false). */
  'debate:voteEnd': (p: { end: boolean }, ack: AckFn) => void;
  /** Host: move on from the Speech or Question Period now. */
  'round:skip': (p: Empty, ack: AckFn) => void;
}

export interface ServerToClientEvents {
  'game:state': (view: GameView) => void;
  'role:privateState': (state: PrivatePlayerState) => void;
  'game:started': () => void;
  'game:over': (p: { winner: Faction; winReason: WinReason }) => void;

  /** Host only. */
  'seat:reclaimRequested': (r: ReclaimRequest) => void;
  /** Host only: a pending request was approved, denied or expired. */
  'seat:reclaimClosed': (p: { requestId: string }) => void;
  /** Requester only: the new credential. */
  'seat:reclaimed': (s: SessionInfo) => void;
  'seat:reclaimDenied': (p: { requestId: string }) => void;

  /** This browser no longer holds its seat. */
  'session:ended': (p: { reason: SessionEndReason }) => void;
}

export const ErrorCode = {
  BAD_REQUEST: 'BAD_REQUEST',
  NOT_IN_GAME: 'NOT_IN_GAME',
  BAD_TOKEN: 'BAD_TOKEN',
  RATE_LIMITED: 'RATE_LIMITED',
  NOT_HOST: 'NOT_HOST',
  INTERNAL: 'INTERNAL',
} as const;
